/**
 * useConfirm — imperative confirmation dialog (Safari-safe replacement for window.confirm).
 *
 * Usage:
 *   1. Wrap your app with <ConfirmProvider> (in App.js)
 *   2. In any component:
 *        const confirm = useConfirm();
 *        const ok = await confirm("Are you sure?");
 *        // or with title:
 *        const ok = await confirm({ title: "Delete", message: "Are you sure?", destructive: true });
 */
import { createContext, useContext, useState, useCallback, useRef } from "react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const ConfirmContext = createContext(null);

export function ConfirmProvider({ children }) {
  const [state, setState] = useState({ open: false, title: "", message: "", destructive: false });
  const resolveRef = useRef(null);

  const confirm = useCallback((opts) => {
    const normalized =
      typeof opts === "string" ? { title: "", message: opts, destructive: false } : opts;
    setState({
      open: true,
      title: normalized.title || "",
      message: normalized.message || "",
      destructive: normalized.destructive ?? false,
      confirmLabel: normalized.confirmLabel || "",
      cancelLabel: normalized.cancelLabel || "",
    });
    return new Promise((resolve) => {
      resolveRef.current = resolve;
    });
  }, []);

  const handleConfirm = () => {
    setState((s) => ({ ...s, open: false }));
    resolveRef.current?.(true);
  };

  const handleCancel = () => {
    setState((s) => ({ ...s, open: false }));
    resolveRef.current?.(false);
  };

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      <AlertDialog open={state.open} onOpenChange={(open) => { if (!open) handleCancel(); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            {state.title && <AlertDialogTitle>{state.title}</AlertDialogTitle>}
            {!state.title && <AlertDialogTitle>Potwierdzenie</AlertDialogTitle>}
            <AlertDialogDescription>{state.message}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={handleCancel}>
              {state.cancelLabel || "Anuluj"}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirm}
              className={state.destructive ? "bg-destructive text-destructive-foreground hover:bg-destructive/90" : ""}
            >
              {state.confirmLabel || "OK"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </ConfirmContext.Provider>
  );
}

export function useConfirm() {
  const ctx = useContext(ConfirmContext);
  if (!ctx) throw new Error("useConfirm must be used within <ConfirmProvider>");
  return ctx;
}
